import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container } from "reactstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FetchPayoutDetail,
  UpdatePayoutStatus,
  SendBankDetailsIssueSms,
} from "../../apis/Payout";
import {
  getPayoutStatusBadge,
  formatCurrency,
  formatDate,
} from "./payoutHelpers";

const PayoutDetail = () => {
  const { payoutId } = useParams();

  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [smsCooldownUntil, setSmsCooldownUntil] = useState(() => {
    const stored = localStorage.getItem(
      `sms_cooldown_bankDetailsIssue_${payoutId}`,
    );
    if (!stored) return null;
    const until = parseInt(stored, 10);
    if (until > Date.now()) return until;
    localStorage.removeItem(`sms_cooldown_bankDetailsIssue_${payoutId}`);
    return null;
  });
  const [smsCountdown, setSmsCountdown] = useState(() => {
    const stored = localStorage.getItem(
      `sms_cooldown_bankDetailsIssue_${payoutId}`,
    );
    if (!stored) return 0;
    const remaining = Math.ceil((parseInt(stored, 10) - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  });

  // Form inputs for status transitions
  const [bankReference, setBankReference] = useState("");
  const [failureReason, setFailureReason] = useState("");
  const [notes, setNotes] = useState("");

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await FetchPayoutDetail(payoutId);
      if (res.success) {
        setPayout(res.data?.payout);
      } else {
        toast.error("Failed to fetch payout details.");
      }
    } catch (error) {
      toast.error("Failed to fetch payout details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutId]);

  const handleUpdateStatus = async (status) => {
    const data = { status };
    if (status === "completed" && bankReference) {
      data.bankReference = bankReference;
    }
    if (status === "failed" && failureReason) {
      data.failureReason = failureReason;
    }
    if (notes) {
      data.notes = notes;
    }

    try {
      setActionLoading(true);
      const res = await UpdatePayoutStatus(payoutId, data);
      if (res.success) {
        toast.success(`Payout marked as ${status}.`);
        fetchDetail(); // Refresh
        setBankReference("");
        setFailureReason("");
        setNotes("");
      } else {
        toast.error(res.message || `Failed to mark payout as ${status}.`);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || `Failed to mark payout as ${status}.`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (!smsCooldownUntil) return;
    const interval = setInterval(() => {
      const remaining = Math.ceil((smsCooldownUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        localStorage.removeItem(`sms_cooldown_bankDetailsIssue_${payoutId}`);
        setSmsCooldownUntil(null);
        setSmsCountdown(0);
        clearInterval(interval);
      } else {
        setSmsCountdown(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [smsCooldownUntil, payoutId]);

  const handleSendBankDetailsIssueSms = async () => {
    try {
      setSmsSending(true);
      const res = await SendBankDetailsIssueSms(payoutId);
      if (res.success) {
        toast.success("SMS sent to host successfully.");
        const until = Date.now() + 5 * 60 * 1000;
        localStorage.setItem(
          `sms_cooldown_bankDetailsIssue_${payoutId}`,
          String(until),
        );
        setSmsCooldownUntil(until);
        setSmsCountdown(300);
      } else {
        toast.error(res.message || "Failed to send SMS.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send SMS.");
    } finally {
      setSmsSending(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          </div>
        </Container>
      </div>
    );
  }

  if (!payout) {
    return (
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6">
            <p>Payout not found.</p>
            <Link
              to="/payouts/history"
              className="text-secondaryColor hover:underline"
            >
              ← Back to Payout History
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  const canProcess = payout.status === "pending";
  const canComplete = payout.status === "processing";
  const canFail = payout.status === "processing";
  const canCancel = payout.status === "pending";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <Link
                to="/payouts/history"
                className="text-secondaryColor hover:underline text-sm"
              >
                ← Back to History
              </Link>
              <h1 className="text-2xl dark:text-[#c7d8eb]">Payout Detail</h1>
            </div>

            {/* Payout Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">
                  Payout Information
                </h3>
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Payout ID
                    </span>
                    <span className="font-mono">{payout._id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Status
                    </span>
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${getPayoutStatusBadge(
                        payout.status,
                      )}`}
                    >
                      {payout.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Total Amount
                    </span>
                    <span>{formatCurrency(payout.totalAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Platform Fees
                    </span>
                    <span className="text-red-500">
                      {formatCurrency(payout.totalPlatformFee)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Net Amount (Host Receives)
                    </span>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(payout.netAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Currency
                    </span>
                    <span>{payout.currency || "SAR"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Created At
                    </span>
                    <span>{formatDate(payout.createdAt)}</span>
                  </div>
                  {payout.paidAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Paid At
                      </span>
                      <span>{formatDate(payout.paidAt)}</span>
                    </div>
                  )}
                  {payout.bankReference && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Bank Reference
                      </span>
                      <span className="font-mono">{payout.bankReference}</span>
                    </div>
                  )}
                  {payout.failureReason && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Failure Reason
                      </span>
                      <span className="text-red-500">
                        {payout.failureReason}
                      </span>
                    </div>
                  )}
                  {payout.notes && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Notes
                      </span>
                      <span>{payout.notes}</span>
                    </div>
                  )}
                  {payout.createdBy && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Created By
                      </span>
                      <span>
                        {payout.createdBy?.firstName}{" "}
                        {payout.createdBy?.lastName}
                      </span>
                    </div>
                  )}
                  {payout.processedBy && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Processed By
                      </span>
                      <span>
                        {payout.processedBy?.firstName}{" "}
                        {payout.processedBy?.lastName}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Host & Bank Info */}
              <div className="flex flex-col gap-4">
                <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-3">Host</h3>
                  <div className="flex flex-col gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Name
                      </span>
                      <span>
                        {payout.host?.firstName} {payout.host?.lastName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Email
                      </span>
                      <span>{payout.host?.email}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-3">Bank Account</h3>
                  {payout.bankAccount ? (
                    <div className="flex flex-col gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Bank Name
                        </span>
                        <span>{payout.bankAccount?.bankName || "-"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          IBAN
                        </span>
                        <span className="font-mono">
                          {payout.bankAccount?.IbanNumber || "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Swift Number
                        </span>
                        <span>{payout.bankAccount?.swiftNumber || "-"}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">
                      No bank account information.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Included Reservations */}
            <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-3">
                Included Reservations ({payout.reservations?.length || 0})
              </h3>

              {!payout.reservations || payout.reservations.length === 0 ? (
                <p className="text-gray-500 text-sm">No reservations.</p>
              ) : (
                <>
                  {/* Desktop */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-500 dark:border-white">
                          <th className="py-2 px-2 text-center">Reservation</th>
                          <th className="py-2 px-2 text-center">Property</th>
                          <th className="py-2 px-2 text-center">Amount</th>
                          <th className="py-2 px-2 text-center">
                            Platform Fee
                          </th>
                          <th className="py-2 px-2 text-center">Host Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payout.reservations.map((item, idx) => (
                          <tr
                            key={idx}
                            className="border-b border-gray-300 dark:border-gray-600"
                          >
                            <td className="py-2 px-2 text-center">
                              {item.reservation?.code
                                ? `#${item.reservation.code}`
                                : item.reservation?._id?.slice(-8) || "-"}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {item.reservation?.property?.title || "-"}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {formatCurrency(item.amount)}
                            </td>
                            <td className="py-2 px-2 text-center text-red-500">
                              {formatCurrency(item.platformFee)}
                            </td>
                            <td className="py-2 px-2 text-center text-green-600 font-semibold">
                              {formatCurrency(item.hostAmount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile */}
                  <div className="block lg:hidden">
                    {payout.reservations.map((item, idx) => (
                      <div
                        key={idx}
                        className="mb-3 border border-gray-300 dark:border-gray-600 rounded-lg p-3"
                      >
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500">Reservation</span>
                          <span>
                            {item.reservation?.code
                              ? `#${item.reservation.code}`
                              : item.reservation?._id?.slice(-8) || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500">Property</span>
                          <span>
                            {item.reservation?.property?.title || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500">Amount</span>
                          <span>{formatCurrency(item.amount)}</span>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500">Platform Fee</span>
                          <span className="text-red-500">
                            {formatCurrency(item.platformFee)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm font-semibold">
                          <span className="text-gray-500">Host Amount</span>
                          <span className="text-green-600">
                            {formatCurrency(item.hostAmount)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Status Actions */}
            {(canProcess || canComplete || canFail || canCancel) && (
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">Update Status</h3>

                {/* Notes input (always available for pending/processing) */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm dark:bg-darkBg1"
                    rows="2"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes..."
                  />
                </div>

                <div className="flex flex-wrap gap-4">
                  {/* Pending → Processing */}
                  {canProcess && (
                    <button
                      onClick={() => handleUpdateStatus("processing")}
                      disabled={actionLoading}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
                    >
                      {actionLoading ? "Updating..." : "Mark Processing"}
                    </button>
                  )}

                  {/* Pending → Cancelled */}
                  {canCancel && (
                    <button
                      onClick={() => handleUpdateStatus("cancelled")}
                      disabled={actionLoading}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
                    >
                      {actionLoading ? "Updating..." : "Cancel Payout"}
                    </button>
                  )}

                  {/* Processing → Completed */}
                  {canComplete && (
                    <div className="flex flex-col gap-2 w-full lg:w-auto">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          placeholder="Bank reference (optional)"
                          value={bankReference}
                          onChange={(e) => setBankReference(e.target.value)}
                          className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm dark:bg-darkBg1"
                        />
                        <button
                          onClick={() => handleUpdateStatus("completed")}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
                        >
                          {actionLoading ? "Updating..." : "Mark Completed"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Processing → Failed */}
                  {canFail && (
                    <div className="flex flex-col gap-2 w-full lg:w-auto">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          placeholder="Failure reason (optional)"
                          value={failureReason}
                          onChange={(e) => setFailureReason(e.target.value)}
                          className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm dark:bg-darkBg1"
                        />
                        <button
                          onClick={() => handleUpdateStatus("failed")}
                          disabled={actionLoading}
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
                        >
                          {actionLoading ? "Updating..." : "Mark Failed"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bank Details Issue SMS - shown when payout has failed */}
            {payout.status === "failed" && (
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-red-300 dark:border-red-700 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-2 text-red-600 dark:text-red-400">
                  Payout Failed
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Notify the host to update their bank details so the payout can
                  be retried.
                </p>
                <button
                  onClick={handleSendBankDetailsIssueSms}
                  disabled={smsSending || !!smsCooldownUntil}
                  className={`px-4 py-2 rounded text-white text-sm font-semibold ${
                    smsSending || smsCooldownUntil
                      ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                      : "bg-orange-500 hover:bg-orange-600"
                  }`}
                >
                  {smsSending
                    ? "Sending..."
                    : smsCooldownUntil
                      ? `Retry in ${Math.floor(smsCountdown / 60)}m ${smsCountdown % 60}s`
                      : "Send SMS: Update Bank Details"}
                </button>
              </div>
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PayoutDetail;
